import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'store/hooks';
import {
    createDraftOperation,
    CreateDraftOperationPayloadT,
    deleteDraftOperation,
    editDraftOperation,
} from '../../BudgetProposal/Resource/ExpenseIncome/Operations/helpers/operationHelpers';
import { ResourceBudgetProposalAssociationT } from 'routes/BudgetModule/config/types';
import { BudgetBys } from 'business/constants';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';

export const useCreateDraftOperationMutation = ({
    name,
    associations,
    resetModal,
}: {
    name: string;
    associations: ResourceBudgetProposalAssociationT[];
    resetModal: () => void;
}) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    return useMutation({
        mutationFn: () =>
            createDraftOperation({
                payload: {
                    name,
                    associations: associations.map((association) => ({
                        budgetProposalId: association.budgetProposal.id,
                        ...(association.budgetProposal.by ===
                        BudgetBys.GESTIONNAIRE
                            ? {
                                  gestionnaireSoftwareId:
                                      association.resource?.resourceSoftwareId,
                              }
                            : {
                                  orgChartFinanceSoftwareId:
                                      association.resource?.resourceSoftwareId,
                              }),
                    })),
                },
            }),

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['budget_ventilated_data'],
            });
            queryClient.invalidateQueries({
                queryKey: ['draft_operations'],
            });
            resetModal();
            dispatch.vision.notifyUser({
                message: t('createDraftOperation.success', {
                    context: sourceTable,
                }),
                snackType: 'success',
            });
        },

        meta: {
            useGlobalOnError: true,
        },
    });
};

export const useDeleteDraftOperationMutation = ({
    draftOperationSoftwareId,
}: {
    draftOperationSoftwareId: string;
}) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    return useMutation({
        mutationFn: () => deleteDraftOperation({ draftOperationSoftwareId }),

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['draft_operations'],
            });
            dispatch.vision.notifyUser({
                message: t('createDraftOperation.deleteSucess', {
                    context: sourceTable,
                }),
                snackType: 'success',
            });
        },

        meta: {
            useGlobalOnError: true,
        },
    });
};

export const useEditDraftOperationMutation = ({
    name,
    associations,
    draftOperationSoftwareId,
    closeModal,
}: {
    name?: string;
    associations?: ResourceBudgetProposalAssociationT[];
    draftOperationSoftwareId: string;
    closeModal: () => void;
}) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    const payload: Partial<CreateDraftOperationPayloadT> = {};
    if (name) {
        payload.name = name;
    }
    if (associations && associations.length >= 1) {
        payload.associations = associations.map((association) => ({
            budgetProposalId: association.budgetProposal.id,
            ...(association.budgetProposal.by === BudgetBys.GESTIONNAIRE
                ? {
                      gestionnaireSoftwareId:
                          association.resource?.resourceSoftwareId,
                  }
                : {
                      orgChartFinanceSoftwareId:
                          association.resource?.resourceSoftwareId,
                  }),
        }));
    }
    return useMutation({
        mutationFn: () =>
            editDraftOperation({
                draftOperationSoftwareId,
                payload,
            }),

        onSuccess: () => {
            // Check draft_operations query, empty string corresponds to optional args
            queryClient.invalidateQueries({
                queryKey: ['draft_operations'],
            });
            closeModal();
            dispatch.vision.notifyUser({
                message: t('createDraftOperation.editSucess', {
                    context: sourceTable,
                }),
                snackType: 'success',
            });
        },

        meta: {
            useGlobalOnError: true,
        },
    });
};
